/**
 * Phase X.1 — WhatsApp Marketing & Broadcast OS.
 *
 * Tabs:
 *   • Templates  — list, create, sync from Meta, submit for approval, archive
 *   • Campaigns  — list, create (form-based with live preview + audience builder), control
 *   • Analytics  — sent/delivered/read/replied + per-campaign breakdown
 *
 * Route: /admin/marketing
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  MessageCircle, RefreshCw, Plus, Send, Loader2, X, Trash2, Pause, Play, Ban,
  Megaphone, Filter, Users, Eye, CheckCheck, FileText as FileIcon, Shield,
  Tag, BarChart3, Image as ImageIcon, Sparkles, Calendar, Edit3, Copy,
  History as HistoryIcon, AlertTriangle, CheckCircle2, RotateCw,
} from "lucide-react";
import { AudiencesTab, AssetsTab, AICampaignGenerator, ScheduleCampaignModal } from "./MarketingOSExtensions";

const STATUS_BADGE = {
  APPROVED:  "bg-emerald-100 text-emerald-700 border-emerald-200",
  PENDING:   "bg-amber-100 text-amber-700 border-amber-200",
  REJECTED:  "bg-rose-100 text-rose-700 border-rose-200",
  DRAFT:     "bg-gray-100 text-gray-700 border-gray-200",
  ARCHIVED:  "bg-gray-100 text-gray-500 border-gray-200",
  UNKNOWN:   "bg-gray-100 text-gray-700 border-gray-200",
};
const CAMP_STATUS = {
  preparing:  "bg-gray-100 text-gray-700",
  scheduled:  "bg-blue-100 text-blue-700",
  running:    "bg-emerald-100 text-emerald-700",
  paused:     "bg-amber-100 text-amber-700",
  cancelled:  "bg-rose-100 text-rose-700",
  completed:  "bg-indigo-100 text-indigo-700",
};

export default function MarketingOS() {
  const [tab, setTab] = useState("templates");
  return (
    <div className="space-y-5" data-testid="admin-marketing">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1a3c5e] flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-orange-500" /> WhatsApp Marketing OS
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Meta-compliant templates, bulk campaigns, audience builder &amp; analytics.</p>
        </div>
      </div>
      <div className="border-b border-gray-200 flex items-center gap-1" data-testid="mk-tabs">
        {[
          { k: "templates", label: "Templates", Icon: FileIcon },
          { k: "campaigns", label: "Campaigns", Icon: Send },
          { k: "audiences", label: "Audiences", Icon: Users },
          { k: "assets",    label: "Assets",    Icon: ImageIcon },
          { k: "analytics", label: "Analytics", Icon: BarChart3 },
        ].map(t => {
          const Active = t.Icon;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`mk-tab-${t.k}`}
              className={`px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5 border-b-2 -mb-px ${
                tab === t.k ? "border-orange-500 text-orange-700" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Active className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "templates" && <TemplatesTab />}
      {tab === "campaigns" && <CampaignsTab />}
      {tab === "audiences" && <AudiencesTab />}
      {tab === "assets" && <AssetsTab />}
      {tab === "analytics" && <AnalyticsTab />}
    </div>
  );
}

/* ====================== TEMPLATES TAB ====================== */
function TemplatesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [editing, setEditing] = useState(null);
  const [cloning, setCloning] = useState(null);
  const [viewResp, setViewResp] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [validateAllReport, setValidateAllReport] = useState(null);
  const [submitAllJobId, setSubmitAllJobId] = useState(null);
  const [submittingOne, setSubmittingOne] = useState(null);
  const [syncReport, setSyncReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/marketing/templates");
      setRows(r.data.rows || []);
      // Coverage panel — non-blocking
      try {
        const c = await api.get("/admin/marketing/templates/master-coverage");
        setCoverage(c.data);
      } catch (_) { /* coverage is best-effort */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load templates");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const seedMaster = async () => {
    if (!window.confirm("Seed all 35 master WhatsApp templates as DRAFT? Existing templates with the same name will be skipped.")) return;
    setSeeding(true);
    try {
      const r = await api.post("/admin/marketing/templates/seed-master");
      toast.success(`Seed complete — ${r.data.inserted_count} added, ${r.data.skipped_count} already present`, { duration: 8000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    } finally { setSeeding(false); }
  };

  const sync = async (force = false) => {
    setSyncing(true);
    try {
      const r = await api.post(`/admin/marketing/templates/sync${force ? "?force=true" : ""}`);
      const d = r.data || {};
      setSyncReport(d);
      const c = d.counts || {};
      toast.success(
        `Synced ${d.synced_total} from Meta — new: ${c.new || 0}, updated: ${c.updated || 0}, already_exists: ${c.already_exists || 0}, missing: ${c.missing_on_meta || 0}`,
        { duration: 9000 }
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed (check WhatsApp credentials)");
    } finally { setSyncing(false); }
  };

  const submit = async (name) => {
    if (!window.confirm(`Submit "${name}" to Meta for approval?`)) return;
    setSubmittingOne(name);
    try {
      const r = await api.post(`/admin/marketing/templates/${name}/submit`);
      const d = r.data || {};
      if (d.ok) {
        toast.success(`Submitted "${name}" — Meta status: ${d.meta_status || d.stored_status}`);
      } else {
        toast.error(`Rejected: ${d.rejection_reason || "see View Response"}  (fbtrace: ${d.fbtrace_id || "-"})`,
                    { duration: 14000 });
      }
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Submit failed";
      toast.error(detail, { duration: 12000 });
    } finally { setSubmittingOne(null); }
  };

  const resubmit = async (name) => {
    if (!window.confirm(`Resubmit "${name}" to Meta? This will reset status to DRAFT then submit again.`)) return;
    setSubmittingOne(name);
    try {
      const r = await api.post(`/admin/marketing/templates/${name}/resubmit`);
      const d = r.data || {};
      if (d.ok) toast.success(`Resubmitted "${name}" — Meta status: ${d.meta_status || d.stored_status}`);
      else toast.error(`Resubmit rejected: ${d.rejection_reason || "see View Response"}`, { duration: 14000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resubmit failed", { duration: 12000 });
    } finally { setSubmittingOne(null); }
  };

  const validateAll = async () => {
    try {
      const r = await api.post("/admin/marketing/templates/validate-all");
      setValidateAllReport(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validate-all failed");
    }
  };

  const submitAll = async () => {
    if (!window.confirm("Start a bulk Submit All job for every non-archived template? Approved/Pending are skipped by default.")) return;
    try {
      const r = await api.post("/admin/marketing/submit-all/start",
        { skip_approved: true, skip_pending: true });
      setSubmitAllJobId(r.data.job_id);
      toast.success(`Submit-All job started — ${r.data.total} templates queued`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit-All failed to start");
    }
  };

  const validate = async (name) => {
    try {
      const r = await api.post(`/admin/marketing/templates/${name}/validate`);
      if (r.data.ok) toast.success("Template is valid — ready to submit");
      else toast.error("Issues: " + r.data.errors.join(" | "), { duration: 12000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
    }
  };

  const archive = async (name) => {
    if (!window.confirm(`Archive template "${name}"?`)) return;
    try {
      await api.delete(`/admin/marketing/templates/${name}`);
      toast.success("Archived");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="mk-templates">
      {coverage && (
        <div className="bg-white border border-gray-200 rounded-2xl p-3 flex items-center justify-between flex-wrap gap-2" data-testid="mk-tmpl-coverage">
          <div className="flex items-center gap-4 text-xs">
            <div><span className="font-bold text-[#1a3c5e]">Master templates:</span> <span data-testid="mk-tmpl-coverage-seeded">{coverage.seeded_locally} / {coverage.total_master}</span> seeded locally</div>
            <div><span className="font-bold text-[#1a3c5e]">Meta approved:</span> <span data-testid="mk-tmpl-coverage-approved">{coverage.approved_on_meta} / {coverage.total_master}</span></div>
            <div className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 font-bold" data-testid="mk-tmpl-coverage-pct">{coverage.coverage_pct}% production-ready</div>
          </div>
          {coverage.seeded_locally < coverage.total_master && (
            <span className="text-[11px] text-amber-700 font-semibold">Click <strong>Seed Master Templates</strong> to add the {coverage.total_master - coverage.seeded_locally} missing template(s).</span>
          )}
        </div>
      )}
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <button onClick={validateAll} data-testid="mk-tmpl-validate-all-btn"
          className="px-3 py-1.5 bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-700 text-xs font-bold rounded-lg inline-flex items-center gap-1.5">
          <CheckCheck className="w-3.5 h-3.5" /> Validate All
        </button>
        <button onClick={submitAll} data-testid="mk-tmpl-submit-all-btn"
          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg inline-flex items-center gap-1.5">
          <Send className="w-3.5 h-3.5" /> Submit All
        </button>
        <button onClick={seedMaster} disabled={seeding} data-testid="mk-tmpl-seed-btn"
          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg inline-flex items-center gap-1.5 disabled:opacity-60">
          {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Seed Master Templates
        </button>
        <button onClick={() => setShowCreate(true)} data-testid="mk-tmpl-create-btn"
          className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold rounded-lg inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> New template
        </button>
        <button onClick={() => sync(false)} disabled={syncing} data-testid="mk-tmpl-sync-btn"
          className="px-3 py-1.5 bg-white border border-gray-200 text-[#1a3c5e] text-xs font-bold rounded-lg inline-flex items-center gap-1.5 disabled:opacity-60">
          {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Sync from Meta
        </button>
        <button onClick={() => sync(true)} disabled={syncing} data-testid="mk-tmpl-force-sync-btn"
          className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 text-xs font-bold rounded-lg inline-flex items-center gap-1.5 disabled:opacity-60"
          title="Overwrite local components even when locally edited but not yet resubmitted.">
          {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
          Force Re-sync
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-x-auto">
        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500 italic">
            No templates yet. Click <strong>Sync from Meta</strong> to pull existing ones, or <strong>New template</strong> to create one.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] uppercase text-gray-600">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Name</th>
                <th className="text-left px-3 py-2 font-bold">Lang</th>
                <th className="text-left px-3 py-2 font-bold">Category</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="text-left px-3 py-2 font-bold">Last synced</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.name} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`mk-tmpl-${r.name}`}>
                  <td className="px-3 py-2 font-semibold text-[#1a3c5e]">{r.name}</td>
                  <td className="px-3 py-2 text-gray-700">{r.language}</td>
                  <td className="px-3 py-2 text-gray-700">{r.category}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${STATUS_BADGE[r.status] || STATUS_BADGE.UNKNOWN}`} data-testid={`mk-tmpl-status-${r.name}`}>
                      {r.status}
                    </span>
                    {r.status === "REJECTED" && r.rejection_reason && (
                      <div className="mt-1 text-[10px] text-rose-700 italic max-w-[280px] truncate" title={r.rejection_reason} data-testid={`mk-tmpl-reason-${r.name}`}>
                        Reason: {r.rejection_reason}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-500 text-[10px]">{(r.last_synced_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                    <button onClick={() => setEditing(r)} data-testid={`mk-tmpl-edit-${r.name}`}
                      className="px-2 py-0.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-bold rounded inline-flex items-center gap-0.5">
                      <Edit3 className="w-3 h-3" /> Edit
                    </button>
                    <button onClick={() => setCloning(r)} data-testid={`mk-tmpl-clone-${r.name}`}
                      className="px-2 py-0.5 bg-cyan-50 hover:bg-cyan-100 border border-cyan-200 text-cyan-700 text-[10px] font-bold rounded inline-flex items-center gap-0.5">
                      <Copy className="w-3 h-3" /> Clone
                    </button>
                    <button onClick={() => validate(r.name)} data-testid={`mk-tmpl-validate-${r.name}`}
                      className="px-2 py-0.5 bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-700 text-[10px] font-bold rounded">
                      Validate
                    </button>
                    {r.status === "REJECTED" ? (
                      <button onClick={() => resubmit(r.name)} data-testid={`mk-tmpl-resubmit-${r.name}`}
                        className="px-2 py-0.5 bg-amber-100 hover:bg-amber-200 border border-amber-300 text-amber-800 text-[10px] font-bold rounded inline-flex items-center gap-0.5">
                        <RotateCw className="w-3 h-3" /> Resubmit
                      </button>
                    ) : (r.status === "DRAFT") ? (
                      <button onClick={() => submit(r.name)} data-testid={`mk-tmpl-submit-${r.name}`}
                        className="px-2 py-0.5 bg-blue-100 hover:bg-blue-200 border border-blue-300 text-blue-700 text-[10px] font-bold rounded">
                        Submit
                      </button>
                    ) : null}
                    <button onClick={() => setViewResp(r)} data-testid={`mk-tmpl-view-resp-${r.name}`}
                      className="px-2 py-0.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 text-[10px] font-bold rounded inline-flex items-center gap-0.5">
                      <Eye className="w-3 h-3" /> Response
                    </button>
                    <button onClick={() => setHistoryFor(r)} data-testid={`mk-tmpl-history-${r.name}`}
                      className="px-2 py-0.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 text-[10px] font-bold rounded inline-flex items-center gap-0.5">
                      <HistoryIcon className="w-3 h-3" /> History
                    </button>
                    <button onClick={() => archive(r.name)} data-testid={`mk-tmpl-archive-${r.name}`}
                      className="px-2 py-0.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-600 text-[10px] font-bold rounded">
                      Archive
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && <TemplateCreateModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} creating={creating} setCreating={setCreating} />}
      {editing && <TemplateEditModal row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {cloning && <TemplateCloneModal row={cloning} onClose={() => setCloning(null)} onSaved={() => { setCloning(null); load(); }} />}
      {viewResp && <MetaResponseModal row={viewResp} onClose={() => setViewResp(null)} />}
      {historyFor && <SubmissionHistoryModal row={historyFor} onClose={() => setHistoryFor(null)} />}
      {validateAllReport && <ValidateAllModal report={validateAllReport} onClose={() => setValidateAllReport(null)} />}
      {submitAllJobId && <SubmitAllProgressModal jobId={submitAllJobId} onClose={() => { setSubmitAllJobId(null); load(); }} />}
      {syncReport && <SyncReportModal report={syncReport} onClose={() => setSyncReport(null)} />}
    </div>
  );
}

function TemplateCreateModal({ onClose, onSaved, creating, setCreating }) {
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("en_US");
  const [category, setCategory] = useState("MARKETING");
  const [body, setBody] = useState("");
  const [headerText, setHeaderText] = useState("");
  const [footer, setFooter] = useState("");
  const [buttons, setButtons] = useState([]);

  const addBtn = () => setButtons(b => [...b, { type: "QUICK_REPLY", text: "" }]);
  const updBtn = (i, patch) => setButtons(b => b.map((x, k) => k === i ? { ...x, ...patch } : x));
  const delBtn = (i) => setButtons(b => b.filter((_, k) => k !== i));

  const submit = async () => {
    if (!/^[a-z0-9_]{2,64}$/.test(name)) {
      toast.error("Name must be 2–64 chars, lowercase + underscores only");
      return;
    }
    if (!body.trim()) { toast.error("Body required"); return; }
    setCreating(true);
    try {
      const components = [];
      if (headerText) components.push({ type: "HEADER", format: "TEXT", text: headerText });
      components.push({ type: "BODY", text: body });
      if (footer) components.push({ type: "FOOTER", text: footer });
      if (buttons.length) components.push({ type: "BUTTONS", buttons });
      await api.post("/admin/marketing/templates", { name, language, category, components });
      toast.success("Template saved as DRAFT — submit when ready");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="mk-tmpl-create-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><Plus className="w-4 h-4" /> New WhatsApp Template</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Name (lowercase+underscore)"><input data-testid="mk-tmpl-name" value={name} onChange={e => setName(e.target.value)}
            placeholder="diwali_offer_2026" className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" /></Field>
          <Field label="Language">
            <select value={language} onChange={e => setLanguage(e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs">
              {["en_US", "en_GB", "hi", "en", "mr", "gu", "ta", "te", "bn", "pa"].map(l => <option key={l}>{l}</option>)}
            </select>
          </Field>
          <Field label="Category">
            <select data-testid="mk-tmpl-category" value={category} onChange={e => setCategory(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs">
              <option>MARKETING</option><option>UTILITY</option><option>AUTHENTICATION</option>
            </select>
          </Field>
        </div>
        <Field label="Header text (optional)"><input value={headerText} onChange={e => setHeaderText(e.target.value)} placeholder="🎉 Diwali Special"
          className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" /></Field>
        <Field label="Body (use {{1}} {{2}} for variables) *">
          <textarea data-testid="mk-tmpl-body" value={body} onChange={e => setBody(e.target.value)} rows={4}
            placeholder="Hi {{1}}, get 20% off our 3BHK plans this Diwali."
            className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs font-mono" />
        </Field>
        <Field label="Footer (optional)"><input value={footer} onChange={e => setFooter(e.target.value)} placeholder="HouseDesignWala"
          className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" /></Field>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-[10px] uppercase font-bold text-gray-600">Buttons (max 3)</label>
            {buttons.length < 3 && <button onClick={addBtn} className="text-[10px] text-blue-600 font-bold">+ Add</button>}
          </div>
          {buttons.map((b, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <select value={b.type} onChange={e => updBtn(i, { type: e.target.value })}
                className="px-2 py-1 border border-gray-200 rounded text-xs">
                <option>QUICK_REPLY</option><option>URL</option><option>PHONE_NUMBER</option>
              </select>
              <input value={b.text} onChange={e => updBtn(i, { text: e.target.value })} placeholder="Button text (max 25)"
                className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs" />
              {b.type === "URL" && <input value={b.url || ""} onChange={e => updBtn(i, { url: e.target.value })} placeholder="https://…"
                className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs" />}
              {b.type === "PHONE_NUMBER" && <input value={b.phone_number || ""} onChange={e => updBtn(i, { phone_number: e.target.value })} placeholder="+91…"
                className="w-32 px-2 py-1 border border-gray-200 rounded text-xs" />}
              <button onClick={() => delBtn(i)} className="text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        {/* Live preview */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs" data-testid="mk-tmpl-preview">
          <div className="text-[9px] uppercase font-bold text-emerald-700 mb-1">WhatsApp preview</div>
          {headerText && <div className="font-bold text-gray-800">{headerText}</div>}
          <div className="whitespace-pre-wrap text-gray-700 mt-1">{body || <span className="italic text-gray-400">Body…</span>}</div>
          {footer && <div className="text-[10px] text-gray-500 mt-1.5">{footer}</div>}
          {buttons.length > 0 && (
            <div className="mt-2 space-y-1">
              {buttons.map((b, i) => (
                <div key={i} className="bg-white text-center py-1 rounded border border-emerald-200 text-emerald-700 font-bold text-[11px]">{b.text || "—"}</div>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold border border-gray-200 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={creating} data-testid="mk-tmpl-save"
            className="px-3 py-1.5 bg-[#1a3c5e] hover:bg-[#0f2940] text-white text-xs font-bold rounded-lg inline-flex items-center gap-1 disabled:opacity-60">
            {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Save as draft
          </button>
        </div>
      </div>
    </div>
  );
}


/* ====================== EDIT TEMPLATE MODAL ====================== */
function TemplateEditModal({ row, onClose, onSaved }) {
  const initial = useMemo(() => {
    const header = (row.components || []).find(c => c.type === "HEADER");
    const body = (row.components || []).find(c => c.type === "BODY");
    const footer = (row.components || []).find(c => c.type === "FOOTER");
    const btns = (row.components || []).find(c => c.type === "BUTTONS");
    return {
      headerText: header?.text || "",
      bodyText: body?.text || "",
      footerText: footer?.text || "",
      buttons: btns?.buttons || [],
      category: row.category,
      language: row.language,
    };
  }, [row]);
  const [bodyText, setBodyText] = useState(initial.bodyText);
  const [headerText, setHeaderText] = useState(initial.headerText);
  const [footerText, setFooterText] = useState(initial.footerText);
  const [buttons, setButtons] = useState(initial.buttons);
  const [category, setCategory] = useState(initial.category);
  const [saving, setSaving] = useState(false);

  const updBtn = (i, patch) => setButtons(b => b.map((x, k) => k === i ? { ...x, ...patch } : x));
  const delBtn = (i) => setButtons(b => b.filter((_, k) => k !== i));
  const addBtn = () => setButtons(b => [...b, { type: "QUICK_REPLY", text: "" }]);

  const save = async () => {
    if (!bodyText.trim()) { toast.error("Body required"); return; }
    setSaving(true);
    try {
      const components = [];
      if (headerText) components.push({ type: "HEADER", format: "TEXT", text: headerText });
      components.push({ type: "BODY", text: bodyText });
      if (footerText) components.push({ type: "FOOTER", text: footerText });
      if (buttons.length) components.push({ type: "BUTTONS", buttons });
      await api.put(`/admin/marketing/templates/${row.name}`,
                    { name: row.name, language: row.language, category, components });
      toast.success(`"${row.name}" saved — status reset to DRAFT. Ready to resubmit.`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="mk-tmpl-edit-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-[#1a3c5e]">Edit template: {row.name}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-5 space-y-3 text-xs">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-amber-800 text-[11px]">
            Saving will reset status to <strong>DRAFT</strong>. You will need to click <strong>Resubmit</strong> to send to Meta again.
          </div>
          <Field label="Category">
            <select value={category} onChange={e => setCategory(e.target.value)} className="w-full px-2 py-1.5 border rounded">
              <option value="MARKETING">MARKETING</option>
              <option value="UTILITY">UTILITY</option>
              <option value="AUTHENTICATION">AUTHENTICATION</option>
            </select>
          </Field>
          <Field label="Header text (optional, ≤60 chars)">
            <input value={headerText} onChange={e => setHeaderText(e.target.value)} maxLength={60}
              className="w-full px-2 py-1.5 border rounded" />
          </Field>
          <Field label="Body (≤1024 chars; use {{1}}, {{2}} for variables)">
            <textarea value={bodyText} onChange={e => setBodyText(e.target.value)} rows={4}
              className="w-full px-2 py-1.5 border rounded font-mono" />
          </Field>
          <Field label="Footer (optional, ≤60 chars)">
            <input value={footerText} onChange={e => setFooterText(e.target.value)} maxLength={60}
              className="w-full px-2 py-1.5 border rounded" />
          </Field>
          <Field label="Buttons (≤3)">
            <div className="space-y-1">
              {buttons.map((b, i) => (
                <div key={i} className="flex gap-1 items-center">
                  <select value={b.type} onChange={e => updBtn(i, { type: e.target.value })} className="border rounded px-1 py-0.5 text-[10px]">
                    <option>QUICK_REPLY</option><option>URL</option><option>PHONE_NUMBER</option>
                  </select>
                  <input value={b.text} onChange={e => updBtn(i, { text: e.target.value })} placeholder="Button text" className="flex-1 border rounded px-1 py-0.5" />
                  {b.type === "URL" && <input value={b.url || ""} onChange={e => updBtn(i, { url: e.target.value })} placeholder="https://…" className="flex-1 border rounded px-1 py-0.5" />}
                  {b.type === "PHONE_NUMBER" && <input value={b.phone_number || ""} onChange={e => updBtn(i, { phone_number: e.target.value })} placeholder="+91…" className="flex-1 border rounded px-1 py-0.5" />}
                  <button onClick={() => delBtn(i)} className="text-rose-600"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
              {buttons.length < 3 && (
                <button onClick={addBtn} className="text-[11px] text-indigo-700 font-bold">+ Add button</button>
              )}
            </div>
          </Field>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold bg-gray-100 rounded">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="mk-tmpl-edit-save-btn"
            className="px-3 py-1.5 text-xs font-bold bg-indigo-600 text-white rounded inline-flex items-center gap-1 disabled:opacity-60">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Edit3 className="w-3 h-3" />}
            Save (reset to DRAFT)
          </button>
        </div>
      </div>
    </div>
  );
}

/* ====================== CLONE TEMPLATE MODAL ====================== */
function TemplateCloneModal({ row, onClose, onSaved }) {
  const [newName, setNewName] = useState(`${row.name}_v2`);
  const [cloning, setCloning] = useState(false);
  const submit = async () => {
    if (!/^[a-z0-9_]{2,64}$/.test(newName)) {
      toast.error("Name must be lowercase a-z, 0-9, underscore (2..64 chars)");
      return;
    }
    setCloning(true);
    try {
      const r = await api.post(`/admin/marketing/templates/${row.name}/clone`, { new_name: newName });
      toast.success(`Cloned as "${r.data.new_name}" — DRAFT, ready to edit + submit.`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    } finally { setCloning(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="mk-tmpl-clone-modal">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-[#1a3c5e]">Clone template</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-5 text-xs space-y-2">
          <p>Cloning <strong>{row.name}</strong>. The clone will inherit category, language and components, but start at status <strong>DRAFT</strong>.</p>
          <Field label="New name (lowercase, underscores only)">
            <input value={newName} onChange={e => setNewName(e.target.value.toLowerCase().replace(/\s+/g, "_"))}
              data-testid="mk-tmpl-clone-input"
              className="w-full px-2 py-1.5 border rounded font-mono" />
          </Field>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold bg-gray-100 rounded">Cancel</button>
          <button onClick={submit} disabled={cloning} data-testid="mk-tmpl-clone-submit-btn"
            className="px-3 py-1.5 text-xs font-bold bg-cyan-600 text-white rounded inline-flex items-center gap-1 disabled:opacity-60">
            {cloning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />}
            Clone
          </button>
        </div>
      </div>
    </div>
  );
}

/* ====================== META RESPONSE VIEWER MODAL ====================== */
function MetaResponseModal({ row, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/marketing/templates/${row.name}/meta-response`).then(r => setData(r.data)).catch(e => toast.error("Failed to load response"));
  }, [row.name]);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="mk-tmpl-resp-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-[#1a3c5e] inline-flex items-center gap-2">
            <Eye className="w-4 h-4" /> Meta Response — {row.name}
          </h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-5 text-xs space-y-3">
          {!data ? <Loader2 className="w-5 h-5 animate-spin" /> : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Kv k="Status" v={data.status} />
                <Kv k="Meta ID" v={data.meta_id} />
                <Kv k="Last submitted" v={data.last_submitted_at} />
                <Kv k="fbtrace_id" v={data.fbtrace_id} />
                <Kv k="error_code" v={data.error_code} />
                <Kv k="error_subcode" v={data.error_subcode} />
              </div>
              {data.rejection_reason && (
                <div className="bg-rose-50 border border-rose-200 rounded p-2 text-rose-800">
                  <strong>Rejection reason:</strong> {data.rejection_reason}
                </div>
              )}
              {data.error_user_msg && data.error_user_msg !== data.rejection_reason && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
                  <strong>Meta says:</strong> {data.error_user_msg}
                </div>
              )}
              <details open>
                <summary className="cursor-pointer font-bold text-[#1a3c5e]">Submitted payload</summary>
                <pre className="bg-gray-900 text-emerald-200 rounded p-2 mt-1 overflow-auto max-h-60 text-[10px]">
                  {JSON.stringify(data.last_submitted_payload, null, 2)}
                </pre>
              </details>
              <details open>
                <summary className="cursor-pointer font-bold text-[#1a3c5e]">Raw Meta response</summary>
                <pre className="bg-gray-900 text-orange-200 rounded p-2 mt-1 overflow-auto max-h-80 text-[10px]" data-testid="mk-tmpl-resp-raw">
                  {JSON.stringify(data.last_meta_response, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Kv({ k, v }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded p-1.5">
      <div className="text-[9px] uppercase font-bold text-gray-500">{k}</div>
      <div className="font-mono text-[11px] break-all">{v ?? "—"}</div>
    </div>
  );
}

/* ====================== SUBMISSION HISTORY MODAL ====================== */
function SubmissionHistoryModal({ row, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/marketing/templates/${row.name}/history`).then(r => setData(r.data)).catch(_ => toast.error("Failed to load history"));
  }, [row.name]);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="mk-tmpl-history-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-[#1a3c5e] inline-flex items-center gap-2">
            <HistoryIcon className="w-4 h-4" /> Submission History — {row.name}
          </h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-5 text-xs">
          {!data ? <Loader2 className="w-5 h-5 animate-spin" /> : (
            data.count === 0 ? (
              <div className="text-gray-500 italic">No history yet. The next Submit / Edit / Clone will appear here.</div>
            ) : (
              <ol className="space-y-2" data-testid="mk-tmpl-history-list">
                {data.history.map((h, i) => (
                  <li key={h.id || i} className="border-l-2 border-indigo-300 pl-3 py-1">
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-[#1a3c5e] uppercase text-[10px]">{h.action}</div>
                      <div className="text-[10px] text-gray-500">{(h.at || "").replace("T", " ").slice(0, 19)}</div>
                    </div>
                    <div className="text-[11px] text-gray-700">
                      {h.status_before || "—"} → <strong>{h.status_after || "—"}</strong>
                    </div>
                    {(h.fields?.rejection_reason) && (
                      <div className="text-[10px] text-rose-700 italic mt-0.5">Reason: {h.fields.rejection_reason}</div>
                    )}
                    {(h.fields?.fbtrace_id) && (
                      <div className="text-[10px] text-gray-500 font-mono">fbtrace_id: {h.fields.fbtrace_id}</div>
                    )}
                    {h.response?.http_status && (
                      <div className="text-[10px] text-gray-500">HTTP {h.response.http_status}</div>
                    )}
                  </li>
                ))}
              </ol>
            )
          )}
        </div>
      </div>
    </div>
  );
}

/* ====================== VALIDATE-ALL REPORT MODAL ====================== */
function ValidateAllModal({ report, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="mk-tmpl-validateall-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-[#1a3c5e] inline-flex items-center gap-2">
            <CheckCheck className="w-4 h-4" /> Validate-All Report
          </h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-5 text-xs space-y-3">
          <div className="grid grid-cols-4 gap-2">
            <Kpi label="Total" value={report.total} Icon={FileIcon} tone="indigo" />
            <Kpi label="Ready" value={report.ready_count} Icon={CheckCircle2} tone="emerald" />
            <Kpi label="Warnings" value={report.warning_count} Icon={AlertTriangle} tone="amber" />
            <Kpi label="Errors" value={report.error_count} Icon={X} tone="rose" />
          </div>
          {report.errors.length > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded p-3">
              <div className="font-bold text-rose-800 mb-1">✗ Errors — these templates cannot be submitted as-is:</div>
              <ul className="text-[11px] space-y-1">
                {report.errors.map(e => <li key={e.name}><strong>{e.name}</strong> — {e.errors.join("; ")}</li>)}
              </ul>
            </div>
          )}
          {report.warnings.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              <div className="font-bold text-amber-800 mb-1">⚠ Warnings — will be skipped during Submit-All:</div>
              <ul className="text-[11px] space-y-1">
                {report.warnings.map(w => <li key={w.name}><strong>{w.name}</strong> — {w.warning}</li>)}
              </ul>
            </div>
          )}
          {report.ready_count > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
              <div className="font-bold text-emerald-800 mb-1">✓ Ready to submit ({report.ready_count}):</div>
              <div className="text-[11px] font-mono leading-relaxed">
                {report.ready.map(r => r.name).join(", ")}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ====================== SUBMIT-ALL PROGRESS MODAL ====================== */
/* ====================== SYNC REPORT MODAL ====================== */
function SyncBucket({ title, names, tone, testid }) {
  return (
    <div className={`border rounded-lg p-3 ${tone}`} data-testid={testid}>
      <div className="font-bold text-[11px] uppercase tracking-wider mb-1">{title} ({names.length})</div>
      {names.length === 0 ? (
        <div className="text-[10px] italic opacity-60">—</div>
      ) : (
        <div className="text-[10px] font-mono leading-relaxed break-words">{names.join(", ")}</div>
      )}
    </div>
  );
}

function SyncReportModal({ report, onClose }) {
  const c = report.counts || {};
  const r = report.report || {};
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="mk-tmpl-sync-report-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-[#1a3c5e] inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Sync Report — {report.synced_total} templates fetched from Meta
          </h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-5 text-xs space-y-3">
          <div className="grid grid-cols-5 gap-2">
            <Kpi label="New" value={c.new || 0} Icon={Plus} tone="emerald" />
            <Kpi label="Updated" value={c.updated || 0} Icon={RotateCw} tone="indigo" />
            <Kpi label="Already Exists" value={c.already_exists || 0} Icon={CheckCircle2} tone="amber" />
            <Kpi label="Missing on Meta" value={c.missing_on_meta || 0} Icon={AlertTriangle} tone="rose" />
            <Kpi label="Archived Skipped" value={c.archived_skipped || 0} Icon={X} tone="cyan" />
          </div>
          {(c.already_exists || 0) > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-800 text-[11px]">
              <strong>{c.already_exists} duplicate-name recoveries:</strong> these local templates were stuck in DRAFT/REJECTED because Meta already had them. They are now adopted into the local cache with the correct status (APPROVED/PENDING). No further action needed.
            </div>
          )}
          <SyncBucket title="New (inserted)" names={r.new || []} tone="bg-emerald-50 border-emerald-200 text-emerald-800" testid="mk-tmpl-sync-bucket-new" />
          <SyncBucket title="Updated (refreshed)" names={r.updated || []} tone="bg-indigo-50 border-indigo-200 text-indigo-800" testid="mk-tmpl-sync-bucket-updated" />
          <SyncBucket title="Already Exists (duplicate-name recovery)" names={r.already_exists || []} tone="bg-amber-50 border-amber-200 text-amber-800" testid="mk-tmpl-sync-bucket-already" />
          <SyncBucket title="Missing on Meta" names={r.missing_on_meta || []} tone="bg-rose-50 border-rose-200 text-rose-800" testid="mk-tmpl-sync-bucket-missing" />
          <SyncBucket title="Archived (skipped)" names={r.archived_skipped || []} tone="bg-cyan-50 border-cyan-200 text-cyan-800" testid="mk-tmpl-sync-bucket-archived" />
        </div>
      </div>
    </div>
  );
}

function SubmitAllProgressModal({ jobId, onClose }) {
  const [job, setJob] = useState(null);
  const poll = useCallback(async () => {
    try {
      const r = await api.get(`/admin/marketing/submit-all/${jobId}`);
      setJob(r.data);
    } catch (e) { /* swallow — modal user can close manually */ }
  }, [jobId]);
  useEffect(() => {
    poll();
    const id = setInterval(poll, 1000);
    return () => clearInterval(id);
  }, [poll]);

  const pct = job && job.total ? Math.round((job.processed / job.total) * 100) : 0;
  const pause = async () => { await api.post(`/admin/marketing/submit-all/${jobId}/pause`); poll(); };
  const resume = async () => { await api.post(`/admin/marketing/submit-all/${jobId}/resume`); poll(); };
  const cancel = async () => { if (window.confirm("Cancel the Submit-All job?")) { await api.post(`/admin/marketing/submit-all/${jobId}/cancel`); poll(); } };
  const retry = async () => { const r = await api.post(`/admin/marketing/submit-all/${jobId}/retry-failed`); toast.success(`Retrying ${r.data.retried || 0} failed items`); poll(); };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="mk-tmpl-submitall-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-[#1a3c5e] inline-flex items-center gap-2">
            <Send className="w-4 h-4" /> Submit-All Progress
          </h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-5 text-xs space-y-3">
          {!job ? <Loader2 className="w-5 h-5 animate-spin" /> : (
            <>
              <div className="grid grid-cols-5 gap-2">
                <Kpi label="Status" value={job.status} Icon={Send} tone="indigo" />
                <Kpi label="Processed" value={`${job.processed}/${job.total}`} Icon={CheckCheck} tone="cyan" />
                <Kpi label="Success" value={job.success} Icon={CheckCircle2} tone="emerald" />
                <Kpi label="Failed" value={job.failed} Icon={X} tone="rose" />
                <Kpi label="ETA" value={job.eta_seconds == null ? "—" : `${job.eta_seconds}s`} Icon={Calendar} tone="amber" />
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div className="h-full bg-indigo-600 transition-all" style={{ width: `${pct}%` }} data-testid="mk-tmpl-submitall-progress" />
              </div>
              <div className="text-center text-[11px] font-bold text-[#1a3c5e]">{pct}%</div>
              <div className="flex items-center gap-2 justify-center">
                {job.status === "running" && (
                  <button onClick={pause} className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 border border-amber-300 text-amber-800 text-[11px] font-bold rounded inline-flex items-center gap-1" data-testid="mk-tmpl-submitall-pause"><Pause className="w-3 h-3" />Pause</button>
                )}
                {job.status === "paused" && (
                  <button onClick={resume} className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 border border-blue-300 text-blue-700 text-[11px] font-bold rounded inline-flex items-center gap-1" data-testid="mk-tmpl-submitall-resume"><Play className="w-3 h-3" />Resume</button>
                )}
                {["running","paused"].includes(job.status) && (
                  <button onClick={cancel} className="px-3 py-1.5 bg-rose-100 hover:bg-rose-200 border border-rose-300 text-rose-700 text-[11px] font-bold rounded inline-flex items-center gap-1" data-testid="mk-tmpl-submitall-cancel"><Ban className="w-3 h-3" />Cancel</button>
                )}
                {(job.status === "completed" || job.status === "cancelled") && job.failed > 0 && (
                  <button onClick={retry} className="px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 border border-indigo-300 text-indigo-700 text-[11px] font-bold rounded inline-flex items-center gap-1" data-testid="mk-tmpl-submitall-retry"><RotateCw className="w-3 h-3" />Retry failed ({job.failed})</button>
                )}
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded max-h-72 overflow-y-auto">
                <table className="w-full text-[10px]">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1">Template</th>
                      <th className="text-left px-2 py-1">Status</th>
                      <th className="text-left px-2 py-1">HTTP</th>
                      <th className="text-left px-2 py-1">Reason / fbtrace</th>
                      <th className="text-left px-2 py-1">Retries</th>
                    </tr>
                  </thead>
                  <tbody data-testid="mk-tmpl-submitall-items">
                    {(job.items || []).map(it => (
                      <tr key={it.name} className="border-t border-gray-100">
                        <td className="px-2 py-1 font-mono">{it.name}</td>
                        <td className="px-2 py-1">
                          <span className={`px-1.5 py-0.5 rounded font-bold ${
                            it.status === "success" ? "bg-emerald-100 text-emerald-700"
                            : it.status === "failed" ? "bg-rose-100 text-rose-700"
                            : it.status === "sending" ? "bg-blue-100 text-blue-700"
                            : it.status === "skipped" ? "bg-gray-100 text-gray-600"
                            : "bg-amber-100 text-amber-700"}`}>{it.status}</span>
                        </td>
                        <td className="px-2 py-1">{it.http_status || "—"}</td>
                        <td className="px-2 py-1 max-w-[260px] truncate" title={it.rejection_reason || ""}>
                          {it.rejection_reason || "—"}
                          {it.fbtrace_id && <div className="text-[9px] text-gray-500 font-mono">{it.fbtrace_id}</div>}
                        </td>
                        <td className="px-2 py-1">{it.retries || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


/* ====================== CAMPAIGNS TAB ====================== */
function CampaignsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  const [scheduleFor, setScheduleFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/marketing/campaigns");
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load campaigns");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ctrl = async (cid, action) => {
    try {
      await api.post(`/admin/marketing/campaigns/${cid}/${action}`);
      toast.success(`${action}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    }
  };

  const onAIApply = (campaign) => {
    setAiDraft(campaign);
    setShowAI(false);
    setShowCreate(true);
  };

  return (
    <div className="space-y-3" data-testid="mk-campaigns">
      <div className="flex justify-end gap-2">
        <button onClick={() => setShowAI(true)} data-testid="mk-camp-ai-btn"
          className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 border border-purple-300 text-purple-800 text-xs font-bold rounded-lg inline-flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> AI Generate
        </button>
        <button onClick={() => { setAiDraft(null); setShowCreate(true); }} data-testid="mk-camp-create-btn"
          className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold rounded-lg inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> New campaign
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-x-auto">
        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500 italic">No campaigns yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] uppercase text-gray-600">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Name</th>
                <th className="text-left px-3 py-2 font-bold">Type</th>
                <th className="text-left px-3 py-2 font-bold">Template</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="text-right px-3 py-2 font-bold">Queued / Sent / Delivered / Read</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const t = r.totals || {};
                return (
                  <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`mk-camp-${r.id}`}>
                    <td className="px-3 py-2 font-semibold text-[#1a3c5e]">{r.name}</td>
                    <td className="px-3 py-2 text-gray-700">{r.type}</td>
                    <td className="px-3 py-2 text-gray-700 font-mono text-[10px]">{r.template_name}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${CAMP_STATUS[r.status] || "bg-gray-100 text-gray-700"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span className="text-blue-700">{t.queued || 0}</span> · <span className="text-emerald-700">{t.sent || 0}</span> · <span className="text-indigo-700">{t.delivered || 0}</span> · <span className="text-purple-700">{t.read || 0}</span>
                    </td>
                    <td className="px-3 py-2 text-right space-x-1">
                      {r.status === "running" && (
                        <button onClick={() => ctrl(r.id, "pause")} data-testid={`mk-camp-pause-${r.id}`}
                          className="px-2 py-0.5 bg-amber-100 hover:bg-amber-200 border border-amber-300 text-amber-700 text-[10px] font-bold rounded inline-flex items-center gap-0.5">
                          <Pause className="w-2.5 h-2.5" /> Pause
                        </button>
                      )}
                      {r.status === "paused" && (
                        <button onClick={() => ctrl(r.id, "start")} data-testid={`mk-camp-resume-${r.id}`}
                          className="px-2 py-0.5 bg-emerald-100 hover:bg-emerald-200 border border-emerald-300 text-emerald-700 text-[10px] font-bold rounded inline-flex items-center gap-0.5">
                          <Play className="w-2.5 h-2.5" /> Resume
                        </button>
                      )}
                      {!["completed", "cancelled"].includes(r.status) && (
                        <button onClick={() => setScheduleFor(r)} data-testid={`mk-camp-schedule-${r.id}`}
                          className="px-2 py-0.5 bg-blue-100 hover:bg-blue-200 border border-blue-300 text-blue-700 text-[10px] font-bold rounded inline-flex items-center gap-0.5">
                          <Calendar className="w-2.5 h-2.5" /> Schedule
                        </button>
                      )}
                      {!["completed", "cancelled"].includes(r.status) && (
                        <button onClick={() => ctrl(r.id, "cancel")} data-testid={`mk-camp-cancel-${r.id}`}
                          className="px-2 py-0.5 bg-rose-100 hover:bg-rose-200 border border-rose-300 text-rose-700 text-[10px] font-bold rounded inline-flex items-center gap-0.5">
                          <Ban className="w-2.5 h-2.5" /> Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && <CampaignCreateModal aiDraft={aiDraft} onClose={() => { setShowCreate(false); setAiDraft(null); }} onSaved={() => { setShowCreate(false); setAiDraft(null); load(); }} />}
      {showAI && <AICampaignGenerator onClose={() => setShowAI(false)} onApply={onAIApply} />}
      {scheduleFor && <ScheduleCampaignModal campaign={scheduleFor} onClose={() => setScheduleFor(null)} onSaved={load} />}
    </div>
  );
}

function CampaignCreateModal({ onClose, onSaved, aiDraft }) {
  const [name, setName] = useState(aiDraft?.campaign_name || "");
  const [type, setType] = useState("broadcast");
  const [tmplName, setTmplName] = useState("");
  const [language, setLanguage] = useState("en_US");
  const [bodyVars, setBodyVars] = useState("");
  const [audience, setAudience] = useState({
    intent: aiDraft?.recommended_audience?.intent || "",
    city: aiDraft?.recommended_audience?.city || "",
    lead_temperature: aiDraft?.recommended_audience?.lead_temperature || "",
    tags: (aiDraft?.recommended_audience?.tags || []).join(","),
    lead_score_gte: "",
  });
  const [scheduledAt, setScheduledAt] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api.get("/admin/marketing/templates").then(r => setTemplates(r.data.rows || []));
  }, []);

  const buildAudience = () => {
    const a = {};
    if (audience.intent) a.intent = audience.intent;
    if (audience.city) a.city = audience.city;
    if (audience.lead_temperature) a.lead_temperature = audience.lead_temperature;
    if (audience.tags) a.tags = audience.tags.split(",").map(x => x.trim()).filter(Boolean);
    if (audience.lead_score_gte) a.lead_score_gte = Number(audience.lead_score_gte);
    return a;
  };

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post("/admin/marketing/audience-preview", { audience: buildAudience() });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  };

  const create = async () => {
    if (!name || !tmplName) { toast.error("Name + template required"); return; }
    setCreating(true);
    try {
      const r = await api.post("/admin/marketing/campaigns", {
        name, type, template_name: tmplName, language,
        body_variables: bodyVars.split(",").map(x => x.trim()).filter(Boolean),
        audience: buildAudience(),
        scheduled_at: scheduledAt || null,
      });
      toast.success(`Campaign created · ${r.data.totals.queued} queued · ${r.data.totals.suppressed} suppressed`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const approved = templates.filter(t => t.status === "APPROVED");

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="mk-camp-create-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-5 space-y-3 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><Send className="w-4 h-4" /> New Campaign</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        {aiDraft && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 text-[11px] text-purple-900" data-testid="mk-camp-ai-hint">
            <div className="font-bold flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI-suggested copy (use this in your APPROVED template body when submitting to Meta):</div>
            <div className="bg-white border border-purple-200 rounded p-2 mt-1 whitespace-pre-wrap">{aiDraft.body}</div>
            <div className="text-[10px] mt-1 italic">Recommended CTA: {aiDraft.cta?.primary} · Send time: {aiDraft.recommended_send_time}</div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Field label="Campaign name *"><input data-testid="mk-camp-name" value={name} onChange={e => setName(e.target.value)}
            placeholder="Diwali Special 2026" className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" /></Field>
          <Field label="Type">
            <select value={type} onChange={e => setType(e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs">
              {["broadcast","festival","offer","launch","product","reactivation","review_request","referral","announcement","new_service","construction_update","package_promotion","portfolio_promotion","quotation_reminder","payment_reminder","lead_nurturing","custom"].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Template * (must be APPROVED)">
            <select data-testid="mk-camp-template" value={tmplName} onChange={e => { setTmplName(e.target.value); const t = templates.find(x => x.name === e.target.value); if (t) setLanguage(t.language || "en_US"); }}
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs">
              <option value="">— select —</option>
              {approved.map(t => <option key={t.name} value={t.name}>{t.name} ({t.language})</option>)}
            </select>
            {approved.length === 0 && <div className="text-[10px] text-rose-600 mt-0.5">No approved templates yet — create or sync first.</div>}
          </Field>
          <Field label="Schedule (UTC, blank = immediate)">
            <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value ? new Date(e.target.value).toISOString() : "")}
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" />
          </Field>
        </div>
        <Field label="Body variables ({{1}} {{2}}…) — comma-separated">
          <input value={bodyVars} onChange={e => setBodyVars(e.target.value)} placeholder="John, 3BHK, ₹4999"
            className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" />
        </Field>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2">
          <h4 className="text-[11px] font-bold text-[#1a3c5e] flex items-center gap-1"><Filter className="w-3.5 h-3.5" /> Audience filters</h4>
          <div className="grid grid-cols-2 gap-2">
            <input value={audience.intent} onChange={e => setAudience(a => ({ ...a, intent: e.target.value }))} placeholder="intent (e.g. price_inquiry)" data-testid="mk-aud-intent"
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" />
            <input value={audience.city} onChange={e => setAudience(a => ({ ...a, city: e.target.value }))} placeholder="city (e.g. Mumbai)"
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" />
            <select value={audience.lead_temperature} onChange={e => setAudience(a => ({ ...a, lead_temperature: e.target.value }))} data-testid="mk-aud-temp"
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs bg-white">
              <option value="">temperature: any</option><option>hot</option><option>warm</option><option>cold</option>
            </select>
            <input value={audience.lead_score_gte} onChange={e => setAudience(a => ({ ...a, lead_score_gte: e.target.value }))} placeholder="lead score ≥ (0-100)"
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" type="number" />
            <input value={audience.tags} onChange={e => setAudience(a => ({ ...a, tags: e.target.value }))} placeholder="tags (comma-separated)"
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs bg-white col-span-2" />
          </div>
          <div className="flex items-center justify-between">
            <button onClick={doPreview} disabled={previewing} data-testid="mk-aud-preview-btn"
              className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold rounded inline-flex items-center gap-1 disabled:opacity-60">
              {previewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} Preview audience
            </button>
            {preview && (
              <div className="text-xs font-bold text-[#1a3c5e]" data-testid="mk-aud-preview-result">
                <Users className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-blue-600" />
                {preview.total_matched} matched · {Math.min(preview.sample?.length || 0, 5)} previewed below
              </div>
            )}
          </div>
          {preview?.sample?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {preview.sample.slice(0, 5).map(s => (
                <span key={s.id} className="px-1.5 py-0.5 rounded-full bg-white border border-blue-200 text-blue-800 text-[10px]">
                  {s.name || "Unnamed"} {s.city ? `· ${s.city}` : ""}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 text-[11px] text-amber-800 flex items-start gap-2">
          <Shield className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Suppression auto-applied: opt-out, complaints, refunds, VIP, AI-disabled, project-active, blocked numbers, and duplicate phones are skipped.</span>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold border border-gray-200 rounded-lg">Cancel</button>
          <button onClick={create} disabled={creating || !tmplName} data-testid="mk-camp-save"
            className="px-3 py-1.5 bg-[#1a3c5e] hover:bg-[#0f2940] text-white text-xs font-bold rounded-lg inline-flex items-center gap-1 disabled:opacity-60">
            {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {scheduledAt ? "Schedule" : "Send now"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ====================== ANALYTICS TAB ====================== */
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/marketing/analytics?days=${days}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load analytics");
    } finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="py-10 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  if (!data) return null;
  const t = data.totals || {};
  return (
    <div className="space-y-3" data-testid="mk-analytics">
      <div className="flex items-center justify-end gap-2">
        <select value={days} onChange={e => setDays(Number(e.target.value))} className="px-2 py-1 border border-gray-200 rounded text-xs">
          <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option>
        </select>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        <Kpi label="Campaigns" value={t.campaigns || 0} tone="indigo" Icon={Megaphone} />
        <Kpi label="Sent" value={t.sent || 0} tone="emerald" Icon={Send} />
        <Kpi label="Delivered" value={t.delivered || 0} tone="blue" Icon={CheckCheck} />
        <Kpi label="Read" value={t.read || 0} tone="purple" Icon={Eye} />
        <Kpi label="Replied" value={t.replied || 0} tone="amber" Icon={MessageCircle} />
        <Kpi label="Failed" value={t.failed || 0} tone="rose" Icon={X} />
        <Kpi label="Suppressed" value={t.suppressed || 0} tone="gray" Icon={Shield} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Kpi label="Delivery rate" value={`${data.delivery_rate_percent || 0}%`} tone="emerald" Icon={CheckCheck} big />
        <Kpi label="Reply rate (CTR)" value={`${data.ctr_percent || 0}%`} tone="orange" Icon={Tag} big />
      </div>
      <div className="bg-white border border-gray-200 rounded-2xl overflow-x-auto">
        {data.campaigns?.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500 italic">No campaigns yet in selected window.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] uppercase text-gray-600">
              <tr><th className="text-left px-3 py-2">Campaign</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Status</th><th className="text-right px-3 py-2">Total</th><th className="text-right px-3 py-2">Sent</th><th className="text-right px-3 py-2">Delivered</th><th className="text-right px-3 py-2">Read</th></tr>
            </thead>
            <tbody>
              {data.campaigns.map(c => (
                <tr key={c.id} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-semibold text-[#1a3c5e]">{c.name}</td>
                  <td className="px-3 py-2 text-gray-700">{c.type}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${CAMP_STATUS[c.status] || "bg-gray-100 text-gray-700"}`}>{c.status}</span></td>
                  <td className="px-3 py-2 text-right tabular-nums">{(c.totals || {}).total || 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{(c.totals || {}).sent || 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{(c.totals || {}).delivered || 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{(c.totals || {}).read || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-0.5">
      <label className="block text-[10px] uppercase font-bold text-gray-600">{label}</label>
      {children}
    </div>
  );
}
function Kpi({ label, value, Icon, tone = "indigo", big = false }) {
  const tones = {
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    purple: "bg-purple-50 border-purple-200 text-purple-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    gray: "bg-gray-50 border-gray-200 text-gray-900",
    orange: "bg-orange-50 border-orange-200 text-orange-900",
  };
  return (
    <div className={`${tones[tone]} border rounded-xl p-3 flex items-center gap-2`}>
      <Icon className="w-4 h-4 opacity-70" />
      <div>
        <div className="text-[9px] uppercase font-bold opacity-70">{label}</div>
        <div className={big ? "text-2xl font-extrabold" : "text-lg font-bold"}>{value}</div>
      </div>
    </div>
  );
}
